import React from 'react';
import { X } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Button, FieldLabel, IconButton, Select } from '../../components/Primitives.jsx';
import { SOURCE_COLUMNS } from '../../data.js';
import { ColumnsContext } from '../StepInspector.jsx';

/* eslint-disable */

// ── Sort & rank ────────────────────────────────────────────────────────
// Sort by N columns, each with its own direction. Keys apply in order:
// the first key is primary, the second is a tiebreaker, etc.
export function SortInspector({ step, onChange, columns }) {
  const c = step.config || {};
  // Back-compat: legacy config used a single { column, dir } pair.
  const initialKeys = (c.keys && c.keys.length)
    ? c.keys
    : (c.column ? [{ column: c.column, dir: c.dir || 'asc' }] : []);

  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const setKeys = (keys) => {
    // Drop the legacy single-column fields so they don't fight the new shape.
    const { column, dir, ...rest } = c;
    onChange({ ...step, config: { ...rest, keys } });
  };

  // Pick a sensible default column for newly added keys — first unused one.
  const ctxCols = React.useContext(ColumnsContext);
  const cols = columns || ctxCols || SOURCE_COLUMNS;
  const nextDefaultColumn = () => {
    const used = new Set(initialKeys.map(k => k.column));
    return (cols.find(co => !used.has(co.name)) || cols[0])?.name || '';
  };

  const addKey = () => setKeys([...initialKeys, { column: nextDefaultColumn(), dir: 'asc' }]);
  const removeKey = (i) => setKeys(initialKeys.filter((_, j) => j !== i));
  const updateKey = (i, patch) => setKeys(initialKeys.map((k, j) => j === i ? { ...k, ...patch } : k));
  const moveKey = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= initialKeys.length) return;
    const next = [...initialKeys];
    [next[i], next[j]] = [next[j], next[i]];
    setKeys(next);
  };

  return (
    <div>
      <FieldLabel hint={initialKeys.length > 1 ? 'Top of list applies first; later keys break ties' : 'Add more keys for tiebreakers'}>Sort keys</FieldLabel>

      {initialKeys.length === 0 && (
        <div style={{
          padding: '14px 12px', textAlign: 'center',
          background: C.input, border: `1px dashed ${C.borderDashed}`,
          borderRadius: 8, color: C.fg5, fontSize: '.78rem',
        }}>
          No sort keys yet. Add one below.
        </div>
      )}

      {initialKeys.map((k, i) => (
        <div key={i} style={{
          background: C.input, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: 10, marginTop: i === 0 ? 0 : 8,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {/* Priority badge */}
          <span style={{
            width: 22, height: 22, borderRadius: 4,
            background: 'var(--info-bg)', color: C.accentBright,
            border: `1px solid ${C.accentMuted}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '.72rem', fontWeight: 700,
            flexShrink: 0,
          }}>{i + 1}</span>

          {/* Column picker */}
          <Select
            value={k.column}
            onChange={(v) => updateKey(i, { column: v })}
            options={cols.map(co => ({
              value: co.name,
              label: `${co.name}  ·  ${co.type}`,
            }))}
            style={{ flex: 1, minWidth: 0 }}
          />

          {/* Direction — compact icon-only segmented */}
          <div style={{
            display: 'flex', gap: 1, padding: 2,
            background: C.app, border: `1px solid ${C.borderStrong}`, borderRadius: 6,
            flexShrink: 0,
          }}>
            {[
              { id: 'asc', label: '↑', title: 'Ascending (A→Z, 0→9)' },
              { id: 'desc', label: '↓', title: 'Descending (Z→A, 9→0)' },
            ].map(d => {
              const on = (k.dir || 'asc') === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => updateKey(i, { dir: d.id })}
                  title={d.title}
                  style={{
                    width: 26, height: 24,
                    background: on ? 'var(--info-bg)' : 'transparent',
                    color: on ? C.accentBright : C.fg5,
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: '.9rem', fontWeight: 700,
                    padding: 0,
                  }}
                >{d.label}</button>
              );
            })}
          </div>

          {/* Reorder + remove */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
            <button
              onClick={() => moveKey(i, -1)}
              disabled={i === 0}
              title="Move up"
              style={{
                width: 22, height: 12, padding: 0, lineHeight: 1,
                background: 'transparent', color: i === 0 ? C.fg6 : C.fg4,
                border: 'none', cursor: i === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: '.72rem',
              }}
            >▲</button>
            <button
              onClick={() => moveKey(i, +1)}
              disabled={i === initialKeys.length - 1}
              title="Move down"
              style={{
                width: 22, height: 12, padding: 0, lineHeight: 1,
                background: 'transparent', color: i === initialKeys.length - 1 ? C.fg6 : C.fg4,
                border: 'none', cursor: i === initialKeys.length - 1 ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: '.72rem',
              }}
            >▼</button>
          </div>

          <IconButton title="Remove sort key" onClick={() => removeKey(i)}><X size={14} /></IconButton>
        </div>
      ))}

      <Button kind="ghost" size="sm" onClick={addKey} icon="＋" style={{ marginTop: 10 }}>
        {initialKeys.length === 0 ? 'Add sort key' : 'Add tiebreaker'}
      </Button>

      <Alert kind="info" style={{ marginTop: 12 }}>
        Numeric-looking strings are compared as numbers; everything else
        compares lexically with locale-aware collation.
      </Alert>
    </div>
  );
}
